import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  Search, MapPin, Building2, Star, CheckCircle2, Lock, Filter, 
  Grid3X3, List, SlidersHorizontal, ChevronDown, X, Bed, Bath, SquareIcon, ChevronLeft
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import api from '@/services/api';

// Helper to get full image URL (handles both relative and absolute URLs)
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/')) return `${BACKEND_URL}${url}`;
  return url;
};

const PROPERTY_TYPES = [
  { value: 'all', label: 'All Types' },
  { value: 'apartment', label: 'Apartment' },
  { value: 'villa', label: 'Villa' },
  { value: 'townhouse', label: 'Townhouse' },
  { value: 'penthouse', label: 'Penthouse' },
  { value: 'studio', label: 'Studio' },
  { value: 'duplex', label: 'Duplex' },
  { value: 'land', label: 'Land' },
  { value: 'retail', label: 'Retail' },
  { value: 'commercial', label: 'Commercial' },
  { value: 'office', label: 'Office' },
  { value: 'warehouse', label: 'Warehouse' },
];

const CITIES = ['All Cities', 'Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain'];

// Complete UAE Cities and Areas - Cascading data (#20-24)
const CITIES_AREAS = {
  'All Cities': ['All Areas'],
  Dubai: [
    'All Areas', 'Downtown Dubai', 'Dubai Marina', 'Palm Jumeirah', 'Business Bay', 'JBR', 'JVC (Jumeirah Village Circle)',
    'JVT (Jumeirah Village Triangle)', 'DIFC', 'City Walk', 'Dubai Hills Estate', 'Emirates Hills',
    'Arabian Ranches', 'Arabian Ranches 2', 'Arabian Ranches 3', 'Al Barsha', 'Al Barsha South',
    'Al Quoz', 'Jumeirah', 'Jumeirah Islands', 'Jumeirah Golf Estates', 'Jumeirah Lake Towers',
    'Jumeirah Park', 'Umm Suqeim', 'The Springs', 'The Meadows', 'The Lakes', 'The Greens',
    'The Views', 'Motor City', 'Sports City', 'Dubai Silicon Oasis', 'Dubai Investment Park',
    'Dubai South', 'Dubai Creek Harbour', 'MBR City', 'Meydan', 'Damac Hills', 'Damac Hills 2',
    'Town Square', 'Mudon', 'Remraam', 'Mirdif', 'Al Rashidiya', 'Al Warqa', 'International City',
    'Discovery Gardens', 'Al Furjan', 'Tilal Al Ghaf', 'Bluewaters Island', 'La Mer', 'Pearl Jumeirah',
    'Madinat Jumeirah Living', 'Port de La Mer', 'Emaar Beachfront', 'Dubai Harbour', 'Marsa Al Arab',
    'Sobha Hartland', 'Villanova', 'Dubai Festival City', 'Al Jaddaf', 'Culture Village', 'Deira',
    'Bur Dubai', 'Karama', 'Satwa', 'Oud Metha', 'Healthcare City', 'Academic City', 'Studio City'
  ],
  'Abu Dhabi': [
    'All Areas', 'Saadiyat Island', 'Yas Island', 'Al Reem Island', 'Al Maryah Island', 'Al Raha Beach',
    'Al Reef', 'Al Ghadeer', 'Masdar City', 'Khalifa City A', 'Khalifa City B', 'Mohamed Bin Zayed City',
    'Al Shamkha', 'Shakhbout City', 'Baniyas', 'Al Wathba', 'Al Falah', 'Hydra Village',
    'Al Samha', 'Al Bahia', 'Al Rahba', 'Corniche Area', 'Al Khalidiyah', 'Al Bateen',
    'Tourist Club Area', 'Al Markaziyah', 'Al Mushrif', 'Al Nahyan', 'Al Manaseer', 'Al Muroor',
    'Rawdhat Abu Dhabi', 'Al Musalla', 'Al Maqtaa', 'Officers City', 'Zayed Sports City',
    'Al Raha Gardens', 'Al Reef Villas', 'Al Reef Downtown', 'Bloom Gardens', 'Faya',
    'Yas Acres', 'West Yas', 'Nurai Island', 'Jubail Island', 'Hudayriyat Island',
    'Mamsha Al Saadiyat', 'Louvre Abu Dhabi District', 'Marina Village', 'Al Zeina', 'Al Muneera'
  ],
  Sharjah: [
    'All Areas', 'Al Majaz', 'Al Nahda', 'Al Khan', 'Al Qasba', 'Al Taawun', 'Al Mamzar', 'Al Buhairah Corniche',
    'Al Jubail', 'Al Fisht', 'Al Gharb', 'Al Sharq', 'Muwaileh', 'University City', 'Sharjah Industrial Area',
    'Al Ramla', 'Al Yarmook', 'Al Wahda', 'Al Qadisiya', 'Al Nasserya', 'Al Khezamia', 'Al Ghubaiba',
    'Sharjah Waterfront City', 'Tilal City', 'Aljada', 'Masaar', 'Al Rahmaniya', 'Al Suyoh',
    'Al Sajaa', 'Al Azra', 'Al Noaf', 'Al Qulayaa', 'Al Riqqa', 'Al Falaj', 'Al Mirgab',
    'Samnan', 'Bu Tina', 'Halwan', 'Al Gharayen', 'Al Brashi', 'Al Qarayen'
  ],
  Ajman: [
    'All Areas', 'Ajman Downtown', 'Al Nuaimia', 'Al Rashidiya', 'Al Rumaila', 'Al Hamidiya', 'Al Mowaihat',
    'Al Jurf', 'Al Yasmeen', 'Emirates City', 'Ajman Uptown', 'Al Zahya', 'Al Rawda',
    'Al Bustan', 'Musheiref', 'Al Ittihad', 'Corniche Ajman', 'Al Helio', 'Al Sawan',
    'Al Nakheel', 'Al Owan', 'Al Amerah', 'Al Manama', 'Masfout'
  ],
  'Ras Al Khaimah': [
    'All Areas', 'Al Hamra Village', 'Mina Al Arab', 'Al Marjan Island', 'Dafan Al Nakheel', 'Khuzam',
    'Al Nakheel', 'Al Dhait', 'Al Mamourah', 'Al Qurm', 'Al Turfa', 'Ras Al Khaimah City',
    'Al Mairid', 'Al Riffa', 'Al Rams', 'Ghalilah', 'Shaam', 'Masafi', 'Al Jazeera',
    'Hayat Island', 'Wynn Island', 'Al Qusaidat', 'Al Kharran', 'Sidroh', 'Al Hudaibah'
  ],
  Fujairah: [
    'All Areas', 'Fujairah City', 'Al Faseel', 'Sakamkam', 'Mirbah', 'Al Aqah', 'Dibba Fujairah',
    'Masafi', 'Al Gurfa', 'Al Hayl', 'Qidfa', 'Sharm', 'Al Bidya', 'Corniche Fujairah',
    'Al Madhab', 'Al Ghurfa', 'Rugaylat', 'Zubarah'
  ],
  'Umm Al Quwain': [
    'All Areas', 'Umm Al Quwain City', 'Al Salamah', 'Al Raas', 'Al Khor', 'Al Soor', 'Al Raudah',
    'Al Haditha', 'Al Maidan', 'Falaj Al Mualla', 'Al Abraq', 'Umm Al Quwain Marina',
    'Al Dar Al Baida', 'Al Neefah'
  ]
};

// Dynamic areas based on selected city
const getAreasForCity = (selectedCity) => {
  return CITIES_AREAS[selectedCity] || CITIES_AREAS['All Cities'];
};

export const PropertiesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [listingType, setListingType] = useState(searchParams.get('type') || 'buy');
  const [propertyType, setPropertyType] = useState(searchParams.get('property_type') || 'all');
  const [city, setCity] = useState(searchParams.get('city') || 'All Cities');
  const [area, setArea] = useState(searchParams.get('area') || 'All Areas');
  const [priceRange, setPriceRange] = useState([0, 500000000]);
  const [minBedrooms, setMinBedrooms] = useState(0);
  const [verifiedOnly, setVerifiedOnly] = useState(true);
  
  // Data states
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('grid');

  useEffect(() => {
    fetchProperties();
  }, [listingType, propertyType, city, verifiedOnly]);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      let url = `/properties?limit=50`;
      
      if (listingType !== 'all') {
        url += `&listing_type=${listingType}`;
      }
      if (propertyType !== 'all') {
        url += `&property_type=${propertyType}`;
      }
      if (city !== 'All Cities') {
        url += `&city=${city}`;
      }
      if (verifiedOnly) {
        url += `&verified_only=true`;
      }
      if (priceRange[0] > 0) {
        url += `&min_price=${priceRange[0]}`;
      }
      if (priceRange[1] < 500000000) {
        url += `&max_price=${priceRange[1]}`;
      }
      if (minBedrooms > 0) {
        url += `&min_bedrooms=${minBedrooms}`;
      }
      
      const response = await api.get(url);
      
      // Filter by search query client-side
      let filtered = response.data;
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        filtered = filtered.filter(p => 
          p.title.toLowerCase().includes(query) ||
          p.location.toLowerCase().includes(query) ||
          p.city.toLowerCase().includes(query)
        );
      }
      
      // Filter by area client-side
      if (area !== 'All Areas') {
        filtered = filtered.filter(p => 
          p.location.toLowerCase().includes(area.toLowerCase())
        );
      }
      
      setProperties(filtered);
    } catch (error) {
      console.error('Failed to fetch properties:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    fetchProperties();
  };

  const clearFilters = () => {
    setSearchQuery('');
    setListingType('buy');
    setPropertyType('all');
    setCity('All Cities');
    setArea('All Areas');
    setPriceRange([0, 50000000]);
    setMinBedrooms(0);
    setVerifiedOnly(true);
  };

  const formatPrice = (price, type) => {
    if (price >= 1000000) {
      return `AED ${(price / 1000000).toFixed(1)}M${type === 'rent' ? '/yr' : ''}`;
    }
    return `AED ${price.toLocaleString()}${type === 'rent' ? '/yr' : ''}`;
  };

  const activeFiltersCount = [
    listingType !== 'buy',
    propertyType !== 'all',
    city !== 'All Cities',
    area !== 'All Areas',
    priceRange[0] > 0 || priceRange[1] < 500000000,
    minBedrooms > 0,
    !verifiedOnly
  ].filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background" data-testid="properties-page">
      <div className="noise-overlay" />
      
      {/* Header */}
      <nav className="sticky top-0 z-50 backdrop-blur-xl border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-16">
            {/* Back button and Logo */}
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => navigate(-1)}
                className="btn-gold-metallic-outline text-sm"
                data-testid="back-btn"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              
              <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
                <div className="w-10 h-10 rounded logo-icon-metallic flex items-center justify-center">
                  <span className="font-display text-lg text-primary-foreground font-bold">R</span>
                </div>
                <span className="font-display text-xl font-medium tracking-tight gold-interactive">Rashii</span>
              </div>
            </div>
            
            {/* Search Bar */}
            <div className="flex-1 max-w-xl mx-8">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search properties, locations..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10 bg-secondary/30 border-white/10 focus:border-primary h-10"
                  data-testid="properties-search-input"
                />
              </div>
            </div>
            
            <Button 
              variant="outline" 
              className="btn-gold-metallic-outline"
              onClick={() => navigate('/login')}
            >
              Sign In
            </Button>
          </div>
        </div>
      </nav>
      
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 py-8">
        {/* Page Title */}
        <div className="mb-8">
          <h1 className="font-display text-3xl tracking-tight">Verified Properties</h1>
          <p className="text-muted-foreground mt-1">
            {properties.length} exclusive listings with Rashii Intelligence
          </p>
        </div>
        
        {/* Filters Bar */}
        <div className="flex flex-wrap gap-4 mb-8">
          {/* Listing Type - Sale Only */}
          <div className="flex rounded border border-white/20 overflow-hidden">
            <div
              className="px-4 py-2 text-xs uppercase tracking-widest bg-primary text-primary-foreground"
              data-testid="filter-sale"
            >
              For Sale
            </div>
          </div>
          
          {/* Property Type */}
          <Select value={propertyType} onValueChange={setPropertyType}>
            <SelectTrigger className="w-[150px] bg-transparent border-white/20" data-testid="filter-property-type">
              <SelectValue placeholder="Property Type" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* City */}
          <Select value={city} onValueChange={(val) => { setCity(val); setArea('All Areas'); }}>
            <SelectTrigger className="w-[150px] bg-transparent border-white/20" data-testid="filter-city">
              <SelectValue placeholder="City" />
            </SelectTrigger>
            <SelectContent>
              {CITIES.map(c => (
                <SelectItem key={c} value={c}>{c}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* Area - Cascading based on City */}
          <Select value={area} onValueChange={setArea}>
            <SelectTrigger className="w-[180px] bg-transparent border-white/20" data-testid="filter-area">
              <SelectValue placeholder="Area" />
            </SelectTrigger>
            <SelectContent>
              {getAreasForCity(city).map(a => (
                <SelectItem key={a} value={a}>{a}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          {/* More Filters */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="border-white/20">
                <SlidersHorizontal className="w-4 h-4 mr-2" />
                More Filters
                {activeFiltersCount > 0 && (
                  <Badge className="ml-2 bg-primary text-primary-foreground">{activeFiltersCount}</Badge>
                )}
              </Button>
            </SheetTrigger>
            <SheetContent className="glass-card border-white/10">
              <SheetHeader>
                <SheetTitle className="font-display">Filters</SheetTitle>
              </SheetHeader>
              <div className="space-y-6 mt-6">
                {/* Price Range */}
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Price Range
                  </label>
                  <div className="flex items-center gap-4 mb-2">
                    <span className="text-sm">AED {priceRange[0].toLocaleString()}</span>
                    <span className="text-muted-foreground">-</span>
                    <span className="text-sm">AED {priceRange[1].toLocaleString()}</span>
                  </div>
                  <Slider
                    value={priceRange}
                    onValueChange={setPriceRange}
                    max={500000000}
                    step={1000000}
                    className="mt-2"
                  />
                </div>
                
                {/* Bedrooms */}
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground mb-3 block">
                    Number of Bedrooms
                  </label>
                  <div className="flex gap-2">
                    {[0, 1, 2, 3, 4, 5].map(num => (
                      <button
                        key={num}
                        className={`w-10 h-10 rounded border transition-colors ${
                          minBedrooms === num ? 'bg-primary text-primary-foreground border-primary' : 'border-white/20 hover:border-primary'
                        }`}
                        onClick={() => setMinBedrooms(num)}
                      >
                        {num === 0 ? 'Any' : num}
                      </button>
                    ))}
                  </div>
                </div>
                
                {/* Verified Only Toggle */}
                <div className="flex items-center justify-between">
                  <label className="text-sm">Verified Properties Only</label>
                  <button
                    className={`w-12 h-6 rounded-full transition-colors ${
                      verifiedOnly ? 'bg-primary' : 'bg-secondary'
                    }`}
                    onClick={() => setVerifiedOnly(!verifiedOnly)}
                  >
                    <div className={`w-5 h-5 rounded-full bg-white transition-transform ${
                      verifiedOnly ? 'translate-x-6' : 'translate-x-0.5'
                    }`} />
                  </button>
                </div>
                
                <Separator />
                
                <div className="flex gap-2">
                  <Button variant="outline" className="flex-1" onClick={clearFilters}>
                    Clear All
                  </Button>
                  <Button className="flex-1 bg-primary text-primary-foreground" onClick={handleSearch}>
                    Apply Filters
                  </Button>
                </div>
              </div>
            </SheetContent>
          </Sheet>
          
          {/* Clear Filters */}
          {activeFiltersCount > 0 && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground">
              <X className="w-4 h-4 mr-1" /> Clear
            </Button>
          )}
          
          {/* View Toggle */}
          <div className="ml-auto flex gap-1 border border-white/20 rounded">
            <button
              className={`p-2 ${viewMode === 'grid' ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <Grid3X3 className="w-4 h-4" />
            </button>
            <button
              className={`p-2 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <List className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Properties Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array(6).fill(0).map((_, idx) => (
              <Card key={idx} className="glass-card animate-pulse">
                <div className="h-48 bg-secondary/50" />
                <CardContent className="p-6 space-y-4">
                  <div className="h-4 bg-secondary/50 rounded w-3/4" />
                  <div className="h-3 bg-secondary/50 rounded w-1/2" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : properties.length > 0 ? (
          <div className={viewMode === 'grid' 
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6'
            : 'space-y-4'
          }>
            {properties.map((property) => (
              <Card 
                key={property.id}
                className={`glass-card border-white/10 hover:border-primary/50 transition-all duration-500 overflow-hidden group cursor-pointer ${
                  viewMode === 'list' ? 'flex' : ''
                }`}
                onClick={() => navigate(`/property/${property.id}`)}
                data-testid={`property-card-${property.id}`}
              >
                <div className={`relative overflow-hidden ${viewMode === 'list' ? 'w-72 flex-shrink-0' : 'h-48'}`}>
                  <img 
                    src={getImageUrl(property.images?.[0]) || 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=800'} 
                    alt={property.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute top-3 left-3 flex gap-2">
                    <Badge className="bg-primary text-primary-foreground uppercase text-[10px]">
                      {property.listing_type === 'buy' ? 'For Sale' : 'For Rent'}
                    </Badge>
                    {property.verification_status === 'verified' && (
                      <Badge className="bg-green-600 text-white uppercase text-[10px]">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> Verified
                      </Badge>
                    )}
                  </div>
                  {property.rashii_score && (
                    <div className="absolute top-3 right-3 bg-black/80 backdrop-blur-sm px-3 py-1 rounded">
                      <div className="flex items-center gap-1">
                        <Star className="w-3 h-3 text-primary fill-primary" />
                        <span className="text-sm font-bold text-primary">{property.rashii_score.overall_score}</span>
                      </div>
                    </div>
                  )}
                </div>
                <CardContent className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground mb-2">
                    <MapPin className="w-3 h-3" />
                    {property.location}
                  </div>
                  <h3 className="font-display text-lg mb-2 group-hover:text-primary transition-colors">
                    {property.title}
                  </h3>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-4">
                    {property.bedrooms > 0 && (
                      <span className="flex items-center gap-1"><Bed className="w-4 h-4" /> {property.bedrooms}</span>
                    )}
                    <span className="flex items-center gap-1"><Bath className="w-4 h-4" /> {property.bathrooms}</span>
                    <span className="flex items-center gap-1"><SquareIcon className="w-4 h-4" /> {property.area_sqft.toLocaleString()} sqft</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-xl font-display text-primary">
                      {formatPrice(property.price, property.listing_type)}
                    </span>
                    {property.rashii_score && (
                      <span className="text-xs text-muted-foreground">
                        Yield: {property.rashii_score.rental_yield?.min}-{property.rashii_score.rental_yield?.max}%
                      </span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <div className="text-center py-16">
            <Building2 className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-display text-xl mb-2">No Properties Found</h3>
            <p className="text-muted-foreground mb-6">
              Try adjusting your filters or search criteria
            </p>
            <Button onClick={clearFilters}>Clear Filters</Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PropertiesPage;
